import { __rest } from "tslib";
import { Popover, PopoverContent, PopoverTrigger, } from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import React, { createContext, useContext } from 'react';
import { tv } from 'tailwind-variants';
import { CheckIcon, ChevronBottomSmallIcon } from '@marvelapp/ballpark-icons';
import { BtwButton as Button } from './BtwButton';
import { BtwText } from './BtwText';
import { Stack } from './Stack';
const combobox = tv({
    slots: {
        comboboxTrigger: [
            // Basic resets for the button
            'font-medium',
            // Layout
            'justify-between',
        ],
        comboboxContent: [
            // Base styles for the dropdown container
            'ring-1',
            'ring-gray-600/20',
            'bg-white',
            'rounded-xl',
            'shadow-lg',
            // Layout
            'flex',
            'min-w-[240px]',
            'max-h-[var(--radix-popover-content-available-height)]',
            'h-auto',
            'overflow-y-scroll',
            'w-[var(--radix-popover-trigger-width)]',
            'z-[1000]',
            'items-stretch',
        ],
        comboboxSearchContainer: [
            // Layout
            'p-2.5',
            'flex',
            'border-b',
            'border-gray-600/20',
            'w-full',
        ],
        comboboxSearch: [
            // Basic reset
            'focus:outline-0',
            // Base styles
            'ring-1',
            'ring-gray-300',
            'rounded-md',
            // Typography
            'text-sm',
            // Layout
            'px-3',
            'py-2',
            'w-full',
            // Focus styles
            'focus-visible:ring-gray-600',
            'focus-visible:outline-none',
            'focus-visible:outline-gray-300',
        ],
        comboboxList: [
            'm-2.5',
            'flex',
            'flex-col',
            'min-h-[36px]',
            'h-[var(--cmdk-list-height)]',
            'max-h-[400px]',
            'overflow-y-scroll',
            'relative',
            // Base styling for the groups
            '[&_[cmdk-group]]:pt-2',
            '[&_[cmdk-group]:not(:last-child)]:border-b',
            '[&_[cmdk-group]:not(:last-child)]:border-gray-600/10',
            '[&_[cmdk-group]:not(:first-child)]:pt-4',
            '[&_[cmdk-group]:not(:last-child)]:pb-2.5',
            // Styles for the group headings
            '[&_[cmdk-group-heading]]:font-body',
            '[&_[cmdk-group-heading]]:font-bold',
            '[&_[cmdk-group-heading]]:uppercase',
            '[&_[cmdk-group-heading]]:px-3',
            '[&_[cmdk-group-heading]]:pb-1',
            '[&_[cmdk-group-heading]]:text-gray-500',
            '[&_[cmdk-group-heading]]:text-xxs',
        ],
        comboboxItem: [
            // Reset
            'appearance-none',
            'bg-transparent',
            'border-none',
            'cursor-pointer',
            'outline-none',
            // Layout
            'pl-3',
            'pt-2',
            'pr-2',
            'pb-2',
            'flex',
            'items-center',
            'justify-between',
            'min-w-0',
            'gap-3',
            // Base styles
            'bg-white',
            'rounded-md',
            // Hover styles
            'data-[selected=true]:bg-gray-100',
            'data-[selected=true]:outline-none',
            // Typography
            'text-gray-900',
            'text-sm',
        ],
    },
});
const { comboboxContent, comboboxItem, comboboxTrigger, comboboxSearchContainer, comboboxSearch, comboboxList, } = combobox();
const ComboboxContext = createContext(undefined);
const useComboboxState = () => {
    const comboboxState = useContext(ComboboxContext);
    if (comboboxState === undefined)
        throw new Error('useComboboxState used outside of Provider');
    return comboboxState;
};
function Container(props) {
    const { children } = props;
    const [isOpen, setIsOpen] = React.useState(false);
    return (React.createElement(Popover, { open: isOpen, onOpenChange: setIsOpen },
        React.createElement(ComboboxContext.Provider, { value: { isOpen, setIsOpen } }, children)));
}
function Trigger(props) {
    const { children, className, size, width, variant, isTruncated, hasPlaceholder, buttonTestId } = props, rest = __rest(props, ["children", "className", "size", "width", "variant", "isTruncated", "hasPlaceholder", "buttonTestId"]);
    return (React.createElement(PopoverTrigger, { asChild: true },
        React.createElement(Button, Object.assign({ "data-testid": buttonTestId, size: size, width: width, className: `
          ${comboboxTrigger({ className })}
          ${isTruncated ? 'min-w-0 flex-1' : undefined}
          ${hasPlaceholder ? 'text-gray-500' : undefined}
        `, variant: variant, trailingIcon: React.createElement("span", null,
                React.createElement(ChevronBottomSmallIcon, null)) }, rest),
            React.createElement("span", { className: "truncate" }, children))));
}
function Content(props) {
    const { className, children } = props, rest = __rest(props, ["className", "children"]);
    return (React.createElement(PopoverContent, { className: comboboxContent({ className }), collisionPadding: { top: 70, bottom: 20 }, sideOffset: 4 },
        React.createElement(Command, Object.assign({ className: "flex w-full flex-col items-stretch" }, rest),
            React.createElement(Stack, { className: comboboxSearchContainer() },
                React.createElement(Command.Input, { placeholder: "Search for options\u2026", className: comboboxSearch() })),
            React.createElement(Command.List, { className: comboboxList(), "data-testid": "list-dropdown" },
                React.createElement(Command.Empty, { className: "flex h-[100px] w-full items-center justify-center p-1" },
                    React.createElement(BtwText, { asChild: true, size: "sm" },
                        React.createElement("span", null, "No matches found\u2026"))),
                children))));
}
function Item(props) {
    const { className, children, value, selected, onSelect, testId } = props, rest = __rest(props, ["className", "children", "value", "selected", "onSelect", "testId"]);
    const { setIsOpen } = useComboboxState();
    return (React.createElement(Command.Item, Object.assign({ "data-testid": `combobox-list-item-${testId}`, onSelect: () => {
            onSelect();
            setIsOpen(false);
        }, className: comboboxItem({ className }) }, rest),
        React.createElement(BtwText, { variant: "primary", weight: "medium", className: "w-full flex-1 truncate" }, children),
        selected && React.createElement(CheckIcon, { className: "h-5 w-5 flex-none" })));
}
/**
 * @deprecated use ComboboxV2 instead
 */
export const Combobox = {
    Container,
    Trigger,
    Content,
    Item,
    Group: Command.Group,
};
